import { get as _get } from "lodash";
import { BehaviorSubject, Observable } from "rxjs";
import { filter, map, distinctUntilChanged } from "rxjs/operators";
import { inspect } from "./devtools";

import { IMiddleware, IState, TSource } from "./interfaces";
import { ConnectResponse, EnhancerOptions } from "./interfaces/devtools";
import {
  StringMiddleware,
  BoolMiddleware,
  ObjectMiddleware,
} from "./middlewares";

const DEFAULT_NS = "__default";

export function selectByKey(path: string) {
  const key = normalizePath(path);
  const [root, ...chunks] = key.split(".");

  return function (source: Observable<IState>) {
    return source.pipe(
      map((value) => {
        return _get(
          value.sources,
          `${root}.value${chunks.length > 0 ? "." : ""}${chunks.join(".")}`
        );
      }),
      distinctUntilChanged(),
      filter((v) => typeof v !== "undefined")
    );
  };
}

export function normalizePath(path: string) {
  let [namespace, id] = path.split(":");

  if (!id) {
    [id, namespace] = [namespace, DEFAULT_NS];
  }

  return getKey(id, namespace);
}

export function getKey(id: string, namespace = DEFAULT_NS) {
  return `${namespace}:${id}`;
}

export class Store {
  private static instance?: Store;
  private readonly middlewares: IMiddleware[] = [];
  private readonly $_state: BehaviorSubject<IState> =
    new BehaviorSubject<IState>({
      catalogs: {},
      sources: {},
    });
  readonly $state: Observable<IState> = this.$_state.asObservable();
  private connection?: ConnectResponse;

  private constructor() {
    this.use(StringMiddleware);
    this.use(BoolMiddleware);
    this.use(ObjectMiddleware);
  }

  inspect(opts: EnhancerOptions) {
    if (!this.connection) {
      this.connection = inspect(
        opts.name ?? "Qodly Mobile",
        this.$_state,
        opts
      );
    }
    return this.connection;
  }

  public static getInstance(): Store {
    if (!Store.instance) {
      Store.instance = new Store();
    }

    return Store.instance;
  }

  public static destroy() {
    Store.instance = undefined;
  }

  create(datasource: TSource) {
    const { id, namespace = DEFAULT_NS } = datasource;
    const key = getKey(id, namespace);
    const { value } = this.$_state;
    const found = this.getSourceByKey(key);

    if (typeof found !== "undefined") {
      throw new Error("datasource already exists");
    }

    this.middlewares.forEach(({ create }) => {
      create(datasource, value);
    });

    this.$_state.next({
      ...value,
      sources: {
        ...value.sources,
        [key]: datasource,
      },
    });
    this.$_state.pipe(distinctUntilChanged());
  }

  private getSourceByKey(key: string) {
    return this.$_state.value.sources[key];
  }

  get(path: string): Observable<unknown> {
    const key = normalizePath(path);

    return this.$_state.pipe(map((value) => value.sources[key]));
  }

  set(path: string, value: unknown) {
    const key = normalizePath(path);
    const [root, ...chunks] = key.split(".");
    const found = this.getSourceByKey(root);

    if (!found) return;

    this.middlewares.forEach(({ set }) => {
      set(root, chunks, value, this.$_state);
    });
  }

  unsubscribe() {
    this.$_state.complete();
  }

  private use(middleware: IMiddleware) {
    this.middlewares.push(middleware);
  }
}
